
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Table, Thead, Tbody, Tr, Th, Td } from '../components/UI';
import { inventoryData } from '../data/inventory';
import { StockStatus } from '../types';
import { useTranslation } from '../contexts/LanguageContext';
import { AlertTriangle, TrendingUp } from '../components/Icons';

const WarningLevels: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  // Filter only warning items + column filters
  const filteredItems = useMemo(() => {
    let items = inventoryData.filter(item => item.status === StockStatus.WARNING);
    
    return items.filter(item => {
      return Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true;
        const itemValue = String((item as any)[key] || '').toLowerCase();
        return itemValue.includes((value as string).toLowerCase());
      });
    });
  }, [columnFilters]);

  return (
    <div className="p-6 space-y-6 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={t('warning_levels')} 
        breadcrumbs={[{ label: 'VMI Hub' }, { label: t('warning_levels') }]}
      />

      <Card className="border-l-4 border-l-amber-500 p-6 bg-amber-50/30 dark:bg-amber-900/10 mb-6">
        <div className="flex items-start gap-4">
           <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full text-amber-600 dark:text-amber-400">
              <AlertTriangle size={24} />
           </div>
           <div>
              <h3 className="text-lg font-bold text-amber-700 dark:text-amber-400">Approaching Minimums</h3>
              <p className="text-sm text-amber-600/80 dark:text-amber-400/80 mt-1">
                 {filteredItems.length} items are nearing their minimum stock thresholds. Monitor closely.
              </p>
           </div>
        </div>
      </Card>

      <Card className="overflow-hidden border-t-4 border-t-brand-medium">
        <Table>
          <Thead>
            <Tr isHeader>
              <Th className="align-top">
                <div className="flex flex-col gap-2 pb-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('item_number')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder="Filter..."
                    onChange={(e) => handleColumnFilterChange('itemNumber', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="align-top">
                <div className="flex flex-col gap-2 pb-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('item_name')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder="Filter..."
                    onChange={(e) => handleColumnFilterChange('itemName', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="align-top">
                <div className="flex flex-col gap-2 pb-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('supplier')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder="Filter..."
                    onChange={(e) => handleColumnFilterChange('supplier', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-right align-top">
                 <div className="flex flex-col gap-2 pb-2 items-end h-full">
                    <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('current_stock')}</span>
                    <div className="h-8"></div>
                 </div>
              </Th>
              <Th className="text-right align-top">
                 <div className="flex flex-col gap-2 pb-2 items-end h-full">
                    <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('min_stock')}</span>
                    <div className="h-8"></div>
                 </div>
              </Th>
              <Th className="text-right align-top">
                 <div className="flex flex-col gap-2 pb-2 items-end h-full">
                    <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Buffer Remaining</span>
                    <div className="h-8"></div>
                 </div>
              </Th>
              <Th className="text-right align-top">
                 <div className="flex flex-col gap-2 pb-2 items-end h-full">
                    <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Usage Trend</span>
                    <div className="h-8"></div>
                 </div>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredItems.length > 0 ? filteredItems.map(item => (
              <Tr key={item.id}>
                <Td><span className="font-bold font-mono text-brand-dark dark:text-white">{item.itemNumber}</span></Td>
                <Td>{item.itemName}</Td>
                <Td className="text-gray-500">{item.supplier}</Td>
                <Td className="text-right font-bold text-amber-600">{item.currentStock}</Td>
                <Td className="text-right">{item.minStock}</Td>
                <Td className="text-right font-bold text-amber-600">+{item.currentStock - item.minStock}</Td>
                <Td className="text-right">
                   <div className="flex items-center justify-end gap-2 text-gray-500">
                      <TrendingUp size={16} /> Stable
                   </div>
                </Td>
              </Tr>
            )) : (
              <Tr>
                <Td colSpan={7} className="text-center py-12 text-gray-400 font-bold uppercase tracking-widest">
                   No warning items found matching your filters.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Card>
    </div>
  );
};

export default WarningLevels;
